import { useFuzzySearchList } from '@nozbe/microfuzz/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import type { ForwardTarget } from '@/hooks/forward/use-batch-forward';
import useConversationSessions, { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import useTags from '@/hooks/tags/use-tags';
import { generateSessionKey } from '@/utils/session';
import useContacts from './use-contacts';
import useSortedFriends from './use-sorted-friends';

export type ForwardListItem = {
  imageURL?: string;
  order?: number;
} & ForwardTarget;

export type ForwardListType = 'recent' | 'chats' | 'groups' | 'tags';

export default function useForwardList(defaultType: ForwardListType = 'chats') {
  const queryClient = useQueryClient();
  const { groups } = useContacts();
  const { availableSessions } = useConversationSessions();
  const { tags } = useTags();
  const { friends, sortedFriends, sortBy, setSortBy } = useSortedFriends();
  const [type, setType] = useState<ForwardListType>(defaultType);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRecentSessions = useFuzzySearchList({
    list: availableSessions,
    queryText: searchTerm,
    getText: (item) => {
      if (item.SessionType === 'group') {
        return [item.GroupName];
      }
      const user = friends[item.UserID];
      if (user) {
        return [user.RemarkName, user.FriendNickName, item.NickName];
      }
      return [item.NickName];
    },
    mapResultItem: ({ item }): ForwardListItem => {
      if (item.SessionType === 'group') {
        return {
          key: generateSessionKey('group', item.ID),
          type: 'group',
          groupId: item.ID,
          imageURL: item.GroupImg,
          name: item.GroupName,
        };
      }
      const user = friends[item.UserID];
      return {
        key: generateSessionKey('session', item.ID),
        type: 'user',
        imageURL: item.HeadImg,
        userId: item.UserID,
        name: user?.RemarkName || user?.FriendNickName || item.NickName,
      };
    },
  });

  const filteredGroups = useFuzzySearchList({
    list: Object.values(groups),
    queryText: searchTerm,
    getText: (item) => [item.GroupName],
    mapResultItem: ({ item }): ForwardListItem => ({
      key: generateSessionKey('group', item.ID),
      type: 'group',
      groupId: item.ID,
      imageURL: item.GroupImg,
      name: item.GroupName,
    }),
  });

  const filterFriends = useFuzzySearchList({
    list: sortedFriends,
    queryText: searchTerm,
    getText: (item) => [item.RemarkName, item.FriendNickName],
    mapResultItem: ({ item }): ForwardListItem => ({
      key: generateSessionKey('session', item.ChatSessionID),
      type: 'user',
      userId: item.FriendID,
      imageURL: item.FriendHeadImg,
      name: item.RemarkName || item.FriendNickName,
    }),
  });

  useEffect(() => {
    Promise.allSettled([
      queryClient.refetchQueries(allChatSessionQueryOptions),
      queryClient.refetchQueries(friendsQueryOptions),
      queryClient.refetchQueries(groupsQueryOptions),
    ]).catch(console.error);
  }, [queryClient]);

  let options: ForwardListItem[];

  switch (type) {
    case 'recent':
      options = filteredRecentSessions;
      break;
    case 'groups':
      options = filteredGroups;
      break;
    case 'chats':
      options = filterFriends;
      break;
    case 'tags':
      options = tags.map((tag) => ({
        key: `tag-${tag.Id}`,
        type: 'tag',
        name: `${tag.TagName} (${tag.TagCount})`,
        tagId: tag.Id,
        userIds: tag.TagMember.map((member) => member.Id),
      }));
      break;
    default:
      options = [...filteredRecentSessions, ...filterFriends, ...filteredGroups];
      break;
  }

  return {
    sortBy,
    setSortBy,
    options,
    recentSessions: filteredRecentSessions,
    searchTerm,
    listType: type,
    setSearchTerm,
    setListType: setType,
  } as const;
}
